import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { notify, Toast } from "../../common/Toast/index.js";
import FormFields from "../../common/FormFields/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const Terms = () => {
  const permission = getUserMenuPermission(5);
  const [isTermsData, setIsTermsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    term_Id: 0,
    name: "",
  });

  const userId = sessionStorage.getItem("userId");

  const fields = [
    {
      name: "name",
      label: "Term Name",
      type: "text",
      required: true,
      errorMessage: "Term name is required",
    },
  ];
  const fetchTermsData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `Terms/GetAll?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      setIsTermsData(res.data.terms);
      setFilteredData(res.data.terms);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(
        res.data.terms.length < pageSize ? currentPage : currentPage + 1
      );
    } catch (err) {
      console.error("Error fetching data:", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTermsData();
  }, [currentPage]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddTerms = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add Terms.", "error");
      return;
    }
    setFormData({
      term_Id: "",
      name: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditTerms = async (id) => {
    try {
      const { data } = await axiosInstance.get(`Terms/GetById/${id}`);
      if (data) {
        setFormData({
          term_Id: data.term_Id || "",
          name: data.term_Name || "",
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      notify("Error fetching Term data:", err);
    }
  };
  const handleDeleteTerms = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`Terms/Delete/${selectedId}`);
      fetchTermsData();
      notify("Term deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Term!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    try {
      const payload = isEditing
        ? {
            term_Id: formData.term_Id ? parseInt(formData.term_Id, 10) : 0,
            term_Name: formData.name,
            modified_By: userId,
          }
        : {
            term_Name: formData.name,
            created_By: userId,
          };

      await axiosInstance.post("Terms/SaveUpdate", payload);
      fetchTermsData();
      setIsModelOpen(false);
      notify(
        isEditing ? "Term updated successfully!" : "Term added successfully!",
        "success"
      );
    } catch (err) {
      notify("Error submitting form:", err);
    }
  };
  const fetchFilteredTerms = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `Terms/FilterByName?termName=${query}`
      );
      setFilteredData(res.data);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchTermsData();
    } else {
      fetchFilteredTerms(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchTermsData();
  };
  return (
    <>
      <Toast />
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <input
              type="text"
              className="w-full bg-gray-100 border border-gray-3 py-2 pl-10 pr-10 rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary">
              <LiaSearchPlusSolid className="w-5 h-5" />
            </button>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
              >
                <MdClose className="w-5 h-5" />
              </button>
            )}
          </div>
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2"
              onClick={handleAddTerms}
            >
              + Add Term
            </button>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full text-sm text-left border border-gray rounded-lg">
              <thead className="bg-primary text-white tracking-widest  font-light">
                <tr>
                  <th className="border border-gray-400 px-4 py-3">Name</th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray-400 px-4 py-3 text-center whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((terms) => (
                    <tr
                      key={terms.term_Id}
                      className="border-b hover:bg-gray-50 transition cursor-pointer "
                    >
                      <td className="border border-gray-100 px-4 py-2">
                        {terms.term_Name}
                      </td>

                      {(permission.isUpdate || permission.isDelete) && (
                        <td className="py-3 border border-gray-100 text-center space-x-2">
                          {permission.isDelete && (
                            <button
                              className="bg-red-100 text-red-600 hover:bg-red-200 px-2 py-1 rounded-md"
                              onClick={() => handleDeleteTerms(terms.term_Id)}
                            >
                              <MdDeleteForever className="w-5 h-5" />
                            </button>
                          )}
                          {permission.isUpdate && (
                            <button
                              className="bg-[#e3f2fd] text-primary hover:bg-blue-200 px-2 py-1 rounded-md"
                              onClick={() => handleEditTerms(terms.term_Id)}
                            >
                              <BiSolidEditAlt className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}

        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-6">
            Showing {isTermsData.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Terms" : "Add Terms"}
      >
        <FormFields
          onSubmit={handleSubmit}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>
      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Terms;
